// frontend/src/components/admin/coupon/AllCoupons.js - Fixed version with View Button

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import {
    Ticket,
    Plus,
    Edit,
    Trash2,
    RefreshCw,
    ArrowLeft,
    Calendar,
    ToggleLeft,
    ToggleRight,
    Search,
    FileText,
    Tag,
    Percent,
    DollarSign,
    Eye
} from 'lucide-react';

// Coupon details modal component
const CouponDetailsModal = ({ coupon, onClose }) => {
    if (!coupon) return null;
    
    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-IN', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    };
    
    return (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="coupon-details-modal" role="dialog" aria-modal="true">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" aria-hidden="true" onClick={onClose}></div>
                
                <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
                
                <div className="inline-block w-full max-w-lg p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white rounded-lg shadow-xl sm:align-middle">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-bold text-gray-900 font-['Crimson_Text']">
                            <Ticket className="inline-block w-5 h-5 mr-2 text-[#dc2626]" />
                            Coupon Details
                        </h3>
                        <button 
                            onClick={onClose}
                            className="text-gray-500 hover:text-gray-700 focus:outline-none"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                    
                    <div className="p-4 mt-2 space-y-4 bg-gray-50 rounded-lg">
                        <div className="grid gap-4 sm:grid-cols-2">
                            <div>
                                <p className="text-sm text-gray-500">Coupon Code:</p>
                                <p className="flex items-center mt-1 text-base font-medium">
                                    <Tag className="inline-block w-4 h-4 mr-1 text-[#dc2626]" />
                                    {coupon.code}
                                </p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-500">Coupon Name:</p>
                                <p className="mt-1 text-base font-medium">{coupon.name}</p>
                            </div>
                        </div>
                        
                        <div className="grid gap-4 sm:grid-cols-2">
                            <div>
                                <p className="text-sm text-gray-500">Discount Type:</p>
                                <p className="flex items-center mt-1 text-base font-medium">
                                    {coupon.type === 'percentage' ? (
                                        <><Percent className="inline-block w-4 h-4 mr-1 text-blue-500" /> Percentage</>
                                    ) : (
                                        <><DollarSign className="inline-block w-4 h-4 mr-1 text-green-500" /> Fixed Amount</>
                                    )}
                                </p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-500">Discount Value:</p>
                                <p className="mt-1 text-base font-medium">
                                    {coupon.type === 'percentage' ? `${coupon.value}%` : `₹${coupon.value}`}
                                </p>
                            </div>
                        </div>
                        
                        <div className="grid gap-4 sm:grid-cols-2">
                            <div>
                                <p className="text-sm text-gray-500">Minimum Order Value:</p>
                                <p className="mt-1 text-base font-medium">₹{coupon.minOrderValue}</p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-500">Maximum Discount:</p>
                                <p className="mt-1 text-base font-medium">₹{coupon.maxDiscount}</p>
                            </div>
                        </div>
                        
                        <div className="grid gap-4 sm:grid-cols-2">
                            <div>
                                <p className="text-sm text-gray-500">Valid From:</p>
                                <p className="flex items-center mt-1 text-base font-medium">
                                    <Calendar className="inline-block w-4 h-4 mr-1 text-gray-500" />
                                    {formatDate(coupon.validFrom)}
                                </p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-500">Valid Until:</p>
                                <p className="flex items-center mt-1 text-base font-medium">
                                    <Calendar className="inline-block w-4 h-4 mr-1 text-gray-500" />
                                    {formatDate(coupon.validUntil)}
                                </p>
                            </div>
                        </div>
                        
                        <div className="grid gap-4 sm:grid-cols-2">
                            <div>
                                <p className="text-sm text-gray-500">Status:</p>
                                <p className={`mt-1 text-base font-medium flex items-center ${coupon.isActive ? 'text-green-600' : 'text-gray-500'}`}>
                                    {coupon.isActive ? (
                                        <><ToggleRight className="inline-block w-4 h-4 mr-1" /> Active</>
                                    ) : (
                                        <><ToggleLeft className="inline-block w-4 h-4 mr-1" /> Inactive</>
                                    )}
                                </p>
                            </div>
                            <div>
                                <p className="text-sm text-gray-500">Usage Count:</p>
                                <p className="mt-1 text-base font-medium">{coupon.usageCount || 0}</p>
                            </div>
                        </div>
                        
                        {coupon.description && (
                            <div>
                                <p className="text-sm text-gray-500">Description:</p>
                                <p className="mt-1 text-base">{coupon.description}</p>
                            </div>
                        )}
                    </div>
                    
                    <div className="flex justify-end mt-6 space-x-3">
                        <button 
                            onClick={onClose}
                            className="px-4 py-2 text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200"
                        >
                            Close
                        </button>
                        <button 
                            onClick={() => {
                                onClose();
                                window.location.href = `/admin/coupon/${coupon._id}/edit`;
                            }}
                            className="flex items-center px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                        >
                            <Edit className="w-4 h-4 mr-2" />
                            Edit Coupon
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

const AllCoupons = () => {
    const [coupons, setCoupons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState('all');
    const [viewCoupon, setViewCoupon] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        fetchCoupons();
    }, []);

    const fetchCoupons = async () => {
        try {
            setLoading(true);
            const { data } = await axios.get('/api/v1/admin/coupons', {
                withCredentials: true
            });
            if (data.success) {
                setCoupons(data.coupons);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error fetching coupons');
        } finally {
            setLoading(false);
        }
    };

    const handleViewCoupon = async (id) => {
        try {
            setLoading(true);
            const { data } = await axios.get(`/api/v1/admin/coupon/${id}`, {
                withCredentials: true
            });
            if (data.success) {
                setViewCoupon(data.coupon);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error fetching coupon details');
        } finally {
            setLoading(false);
        }
    };

    const handleToggleStatus = async (id, currentStatus) => {
        try {
            const { data } = await axios.patch(`/api/v1/admin/coupon/${id}/toggle`, {}, {
                withCredentials: true
            });
            if (data.success) {
                setCoupons(prevCoupons =>
                    prevCoupons.map(coupon =>
                        coupon._id === id ? { ...coupon, isActive: data.isActive } : coupon
                    )
                );
                
                toast.success(data.message);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error toggling coupon status');
        }
    };

    const handleDeleteCoupon = async (id) => {
        if (window.confirm('Are you sure you want to delete this coupon?')) {
            try {
                const { data } = await axios.delete(`/api/v1/admin/coupon/${id}`, {
                    withCredentials: true
                });
                if (data.success) {
                    setCoupons(prevCoupons => prevCoupons.filter(coupon => coupon._id !== id));
                    toast.success('Coupon deleted successfully');
                }
            } catch (error) {
                toast.error(error.response?.data?.message || 'Error deleting coupon');
            }
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-IN', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    };

    const getCouponStatusLabel = (coupon) => {
        const now = new Date();
        const validFrom = new Date(coupon.validFrom);
        const validUntil = new Date(coupon.validUntil);
        
        if (!coupon.isActive) {
            return 'Inactive';
        } else if (now < validFrom) {
            return 'Scheduled';
        } else if (now > validUntil) {
            return 'Expired';
        } else {
            return 'Active';
        }
    };
    
    const getCouponStatusClass = (coupon) => {
        const status = getCouponStatusLabel(coupon);
        
        switch (status) {
            case 'Active':
                return 'bg-green-100 text-green-800';
            case 'Inactive':
                return 'bg-gray-100 text-gray-800';
            case 'Scheduled':
                return 'bg-blue-100 text-blue-800';
            case 'Expired':
                return 'bg-red-100 text-red-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const filteredCoupons = coupons.filter(coupon => {
        const matchesSearch = coupon.code.toLowerCase().includes(searchTerm.toLowerCase()) || 
                           coupon.name.toLowerCase().includes(searchTerm.toLowerCase());
        
        if (filterType === 'all') {
            return matchesSearch;
        } else if (filterType === 'active') {
            return matchesSearch && coupon.isActive &&
                   new Date() >= new Date(coupon.validFrom) &&
                   new Date() <= new Date(coupon.validUntil);
        } else if (filterType === 'inactive') {
            return matchesSearch && !coupon.isActive;
        } else if (filterType === 'expired') {
            return matchesSearch && new Date() > new Date(coupon.validUntil);
        }
        
        return matchesSearch;
    });

    if (loading && coupons.length === 0) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <RefreshCw className="h-8 w-8 text-[#dc2626] animate-spin" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 p-4">
            <div className="max-w-7xl mx-auto">
                <div className="flex items-center justify-between mb-6">
                    <button
                        onClick={() => navigate('/admin/dashboard')}
                        className="flex items-center text-gray-600 hover:text-[#dc2626] transition-colors"
                    >
                        <ArrowLeft className="h-5 w-5 mr-1" />
                        <span>Back to Dashboard</span>
                    </button>
                    
                    <button
                        onClick={() => navigate('/admin/coupon/new')}
                        className="bg-[#dc2626] text-white px-4 py-2 rounded-lg hover:bg-[#dc2626]/90 transition-colors flex items-center"
                    >
                        <Plus className="h-5 w-5 mr-1" />
                        Create Coupon
                    </button>
                </div>
                
                <div className="bg-white rounded-xl shadow-lg p-6 overflow-hidden">
                    <h1 className="text-xl font-bold text-gray-900 mb-6 font-['Crimson_Text']">
                        <Ticket className="inline h-5 w-5 mr-2 text-[#dc2626]" />
                        Coupon Management
                    </h1>
                    
                    <div className="flex flex-col md:flex-row gap-4 mb-6">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                            <input
                                type="text"
                                placeholder="Search coupons by code or name..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#dc2626] focus:border-transparent"
                            />
                        </div>
                        
                        <select
                            value={filterType}
                            onChange={(e) => setFilterType(e.target.value)}
                            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#dc2626] focus:border-transparent"
                        >
                            <option value="all">All Coupons</option>
                            <option value="active">Active Now</option>
                            <option value="inactive">Inactive</option>
                            <option value="expired">Expired</option>
                        </select>
                    </div>
                    
                    {filteredCoupons.length === 0 ? (
                        <div className="text-center py-8">
                            <Ticket className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-1">No Coupons Found</h3>
                            <p className="text-gray-500">
                                {searchTerm || filterType !== 'all'
                                    ? 'No coupons match your search criteria.'
                                    : 'You haven\'t created any coupons yet.'}
                            </p>
                            <button
                                onClick={() => navigate('/admin/coupon/new')}
                                className="mt-4 inline-flex items-center px-4 py-2 bg-[#dc2626] text-white rounded-lg hover:bg-[#dc2626]/90 transition-colors"
                            >
                                <Plus className="h-4 w-4 mr-2" />
                                Create Your First Coupon
                            </button>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Code
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Name
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Type
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Value
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Min. Order
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Max Discount
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Validity
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Status
                                        </th>
                                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {filteredCoupons.map((coupon) => (
                                        <tr key={coupon._id} className="hover:bg-gray-50">
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    <Tag className="flex-shrink-0 h-4 w-4 text-[#dc2626] mr-2" />
                                                    <span className="font-medium">{coupon.code}</span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <div className="text-sm">{coupon.name}</div>
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    {coupon.type === 'percentage' ? (
                                                        <Percent className="h-4 w-4 text-blue-500 mr-1" />
                                                    ) : (
                                                        <DollarSign className="h-4 w-4 text-green-500 mr-1" />
                                                    )}
                                                    <span className="capitalize">{coupon.type}</span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                {coupon.type === 'percentage' ? (
                                                    <span>{coupon.value}%</span>
                                                ) : (
                                                    <span>₹{coupon.value}</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                ₹{coupon.minOrderValue}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                ₹{coupon.maxDiscount}
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <div className="flex flex-col">
                                                    <div className="flex items-center text-xs">
                                                        <Calendar className="h-3 w-3 text-gray-400 mr-1" />
                                                        <span>From: {formatDate(coupon.validFrom)}</span>
                                                    </div>
                                                    <div className="flex items-center text-xs mt-1">
                                                        <Calendar className="h-3 w-3 text-gray-400 mr-1" />
                                                        <span>To: {formatDate(coupon.validUntil)}</span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getCouponStatusClass(coupon)}`}>
                                                    {getCouponStatusLabel(coupon)}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 whitespace-nowrap">
                                                <div className="flex items-center space-x-2">
                                                    <button
                                                        onClick={() => handleViewCoupon(coupon._id)}
                                                        className="p-1 text-indigo-600 hover:bg-indigo-50 rounded-full"
                                                        title="View Coupon"
                                                    >
                                                        <Eye className="h-5 w-5" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleToggleStatus(coupon._id, coupon.isActive)}
                                                        className={`p-1 rounded-full ${coupon.isActive ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-50'}`}
                                                        title={coupon.isActive ? 'Deactivate Coupon' : 'Activate Coupon'}
                                                    >
                                                        {coupon.isActive ? (
                                                            <ToggleRight className="h-5 w-5" />
                                                        ) : (
                                                            <ToggleLeft className="h-5 w-5" />
                                                        )}
                                                    </button>
                                                    <button
                                                        onClick={() => navigate(`/admin/coupon/${coupon._id}/edit`)}
                                                        className="p-1 text-blue-600 hover:bg-blue-50 rounded-full"
                                                        title="Edit Coupon"
                                                    >
                                                        <Edit className="h-5 w-5" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteCoupon(coupon._id)}
                                                        className="p-1 text-red-600 hover:bg-red-50 rounded-full"
                                                        title="Delete Coupon"
                                                    >
                                                        <Trash2 className="h-5 w-5" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
            
            {/* Coupon Details Modal */}
            {viewCoupon && (
                <CouponDetailsModal 
                    coupon={viewCoupon} 
                    onClose={() => setViewCoupon(null)} 
                />
            )}
        </div>
    );
};

export default AllCoupons;